import { useQueryClient } from '@tanstack/react-query'
import { debounce } from 'lodash'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { QUERY_KEYS } from 'constants/constants'
import { mutation } from '../graphql'
import { notifyBugsnag } from '../helpers'
import {
  isDraftEqual,
  readAdjustmentDraft,
  setDifficultyInDraft,
  toggleChallengeInDraft,
  toggleCriterionInDraft,
  withOverrideDefaults
} from '../helpers/career-adjustment'

const PERSIST_DEBOUNCE_MS = 800
const PERSIST_MAX_WAIT_MS = 3000

/**
 * Besitzt den Draft des Expertenmodus (career.status === 'WAIT_CONFIG') und
 * schreibt ihn debounced nach career_analyse.override_defaults.
 *
 * Vorbild ist use-table-column-settings.js: der lokale Draft ist die einzige
 * Quelle fuer die Anzeige, damit ein fremder Refetch die Ansicht nie
 * zuruecksetzt, und der optimistische Cache-Write geht vor der Mutation raus.
 * Die Mutation-Response wird nicht in den Cache geschrieben, updateCareer
 * liefert career_analyse als null zurueck (Amplify-Bug, siehe
 * pages/create-career.js).
 *
 * Vier bewusste Abweichungen vom Vorbild:
 *
 * 1. Writes laufen ueber eine serialisierte Promise-Kette. Bei einem
 *    Read-Modify-Write auf einen JSON-Blob ist die Reihenfolge
 *    korrektheitsrelevant, nicht nur eine Anzeigefrage.
 * 2. Nicht ueber careerMutation.mutate aus pages/create-career.js. Der Shim
 *    dort faengt den GraphQL-Fehler und resolved trotzdem, ein
 *    fehlgeschlagener Write waere hier also unsichtbar. Ausserdem refetcht er
 *    bei jedem Write, was pro Toggle eine getCareer-Runde kostet.
 * 3. Kein Rollback per Refetch im Fehlerfall. Der Rollback waere der Verlust
 *    genau der Arbeit, um die es geht. Der optimistische Stand bleibt stehen,
 *    der Nutzer bekommt den Fehlerzustand und kann es erneut versuchen.
 * 4. settle() ist awaitbar. synthesizeAnalyze ersetzt career_analyse
 *    vollstaendig, ein gleichzeitig laufender Write ginge verloren.
 */
export const useCareerAdjustment = ({
  career,
  configuration,
  systemDimensionsData,
  availableExamIds
}) => {
  const queryClient = useQueryClient()

  const options = useMemo(
    () => ({ configuration, systemDimensionsData, availableExamIds }),
    [configuration, systemDimensionsData, availableExamIds]
  )

  const stored = useMemo(
    () => readAdjustmentDraft(career, options),
    [career, options]
  )

  const [draft, setDraft] = useState(stored)
  const [saveState, setSaveState] = useState('idle')
  const [prunedCriterion, setPrunedCriterion] = useState(null)

  const latestDraftRef = useRef(draft)
  const pendingRef = useRef(false)
  // Zaehler statt Boolean: ueberlappende Writes (maxWait, langsames Netz)
  // duerfen den Re-Sync erst freigeben, wenn der letzte gesettelt ist.
  const inFlightRef = useRef(0)
  const chainRef = useRef(Promise.resolve())
  const lastWriteOkRef = useRef(true)
  const frozenRef = useRef(false)
  const mountedRef = useRef(true)

  // Muss-Kriterien, die beim Abwaehlen einer Challenge automatisch entfernt
  // wurden. Wird dieselbe Challenge in derselben Session wieder angewaehlt,
  // kommen sie zurueck, damit ein versehentlicher Klick nichts kostet.
  const prunedByChallengeRef = useRef({})

  const careerRef = useRef(career)
  careerRef.current = career
  const optionsRef = useRef(options)
  optionsRef.current = options

  // Re-Sync: Aenderungen von aussen uebernehmen (Refetch, anderer Tab), aber
  // nie, solange ein eigener Write pending oder in flight ist.
  useEffect(() => {
    if (pendingRef.current || inFlightRef.current > 0) return
    if (isDraftEqual(latestDraftRef.current, stored)) return

    latestDraftRef.current = stored
    setDraft(stored)
  }, [stored])

  const writeOnce = useCallback(async () => {
    const { id } = careerRef.current
    const queryKey = QUERY_KEYS.createCareerById(id)

    // Frisch aus dem Cache parsen statt aus einem Mount-Snapshot: so
    // ueberleben Top-Level-Felder, die zwischenzeitlich jemand anders
    // geschrieben hat.
    const cached = queryClient.getQueryData(queryKey)
    const career_analyse = withOverrideDefaults(
      cached?.career_analyse ?? careerRef.current.career_analyse,
      latestDraftRef.current
    )

    // Laufenden getCareer verwerfen, seine spaete Antwort wuerde den
    // optimistischen Eintrag ueberschreiben.
    queryClient.cancelQueries({ queryKey })
    queryClient.setQueryData(queryKey, (prev) =>
      prev ? { ...prev, career_analyse } : prev
    )

    await mutation({ mutation: 'updateCareer', input: { id, career_analyse } })
  }, [queryClient])

  const enqueue = useCallback(() => {
    pendingRef.current = false
    inFlightRef.current += 1
    if (mountedRef.current) setSaveState('saving')

    // Ein vorheriger Fehler darf den naechsten Write nicht blockieren, der
    // schreibt ohnehin den vollstaendigen Draft und heilt ihn damit.
    const next = chainRef.current.catch(() => undefined).then(writeOnce)

    chainRef.current = next.then(
      () => {
        lastWriteOkRef.current = true
        inFlightRef.current = Math.max(0, inFlightRef.current - 1)
        if (
          mountedRef.current &&
          inFlightRef.current === 0 &&
          !pendingRef.current
        ) {
          setSaveState('saved')
        }
      },
      (err) => {
        lastWriteOkRef.current = false
        inFlightRef.current = Math.max(0, inFlightRef.current - 1)
        notifyBugsnag(err)
        if (mountedRef.current) setSaveState('error')
      }
    )

    return chainRef.current
  }, [writeOnce])

  const enqueueRef = useRef(enqueue)
  enqueueRef.current = enqueue

  const persist = useMemo(
    () =>
      debounce(() => enqueueRef.current(), PERSIST_DEBOUNCE_MS, {
        trailing: true,
        maxWait: PERSIST_MAX_WAIT_MS
      }),
    []
  )

  // Offene Aenderungen beim Verlassen der Seite und beim Unmount schreiben.
  useEffect(() => {
    mountedRef.current = true
    const onPageHide = () => {
      if (!frozenRef.current) persist.flush()
    }
    window.addEventListener('pagehide', onPageHide)

    return () => {
      mountedRef.current = false
      window.removeEventListener('pagehide', onPageHide)
      if (!frozenRef.current) persist.flush()
    }
  }, [persist])

  const commit = useCallback(
    (next) => {
      if (frozenRef.current) return

      latestDraftRef.current = next
      pendingRef.current = true
      setDraft(next)
      setSaveState('saving')
      persist()
    },
    [persist]
  )

  const toggleChallenge = useCallback(
    (exam_id) => {
      const current = latestDraftRef.current
      const wasSelected = current.selected_challenges.some(
        (challenge) => challenge.exam_id === exam_id
      )

      const { draft: next, pruned } = toggleChallengeInDraft(current, exam_id, {
        ...optionsRef.current,
        restoreCriteria: wasSelected
          ? []
          : prunedByChallengeRef.current[exam_id] || []
      })

      if (next === current) return

      if (pruned.length) prunedByChallengeRef.current[exam_id] = pruned
      else if (!wasSelected) delete prunedByChallengeRef.current[exam_id]

      setPrunedCriterion(pruned[0] || null)
      commit(next)
    },
    [commit]
  )

  const setSelectionDifficulty = useCallback(
    (difficulty) => {
      if (difficulty === latestDraftRef.current.selection_difficulty) return

      setPrunedCriterion(null)
      commit(setDifficultyInDraft(latestDraftRef.current, difficulty))
    },
    [commit]
  )

  const toggleCriterion = useCallback(
    (dimensionKey) => {
      setPrunedCriterion(null)
      commit(toggleCriterionInDraft(latestDraftRef.current, dimensionKey))
    },
    [commit]
  )

  /**
   * Schreibt den aktuellen Draft erneut und wartet, bis die Kette gesettelt
   * ist. Der Write traegt immer den vollstaendigen Draft und heilt damit
   * einen zuvor fehlgeschlagenen Autosave.
   *
   * Im gefrorenen Zustand ein No-op. Der Fehler-Chip bleibt sichtbar, waehrend
   * complete() laeuft, denn dessen eigener Write geht an diesem Hook vorbei
   * und setzt saveState nicht zurueck. Ein Klick auf "Erneut speichern" in
   * diesem Fenster wuerde sonst einen updateCareer gegen synthesizeAnalyze
   * rennen lassen. Und weil writeOnce den Blob aus dem react-query-Cache baut,
   * in dem das Ergebnis des Lambdas nie landet, wuerde dieser Write die
   * gefaltete Analyse mit dem Stand davor ueberschreiben. Auf einer
   * Preset-Stelle ist das nicht heilbar, synthesizeAnalyze hat
   * career_preset_id dann bereits entfernt und wirft beim zweiten Lauf.
   *
   * complete() braucht diesen Weg nicht, es schreibt den Draft selbst.
   *
   * @returns Promise<boolean> false, wenn nicht geschrieben wurde oder der
   *          Write fehlgeschlagen ist
   */
  const retry = useCallback(async () => {
    if (frozenRef.current) return false

    persist.cancel()
    pendingRef.current = true
    enqueueRef.current()

    await chainRef.current.catch(() => undefined)

    return lastWriteOkRef.current
  }, [persist])

  /**
   * Stoppt jeden weiteren Hintergrund-Write und wartet, bis die laufenden
   * gesettelt sind. Eine noch nicht abgeschickte Aenderung wird bewusst NICHT
   * mehr geschrieben: der Aufrufer schreibt danach selbst, entweder den
   * vollstaendigen Draft (complete) oder null (abort).
   *
   * synthesizeAnalyze ersetzt career_analyse vollstaendig, und der Null-Write
   * beim Abbrechen wuerde von einem nachlaufenden Debounce wieder aufgehoben.
   * Beide Fenster schliesst dieses Warten.
   */
  const freeze = useCallback(async () => {
    frozenRef.current = true
    persist.cancel()
    pendingRef.current = false

    await chainRef.current.catch(() => undefined)
  }, [persist])

  const unfreeze = useCallback(() => {
    frozenRef.current = false
  }, [])

  const selectedExamIds = useMemo(
    () => draft.selected_challenges.map(({ exam_id }) => exam_id),
    [draft]
  )

  return {
    draft,
    draftRef: latestDraftRef,
    selectedExamIds,
    dimensions: draft.dimensions,
    selectionDifficulty: draft.selection_difficulty,
    mandatoryCriteria: draft.mandatory_criteria,

    saveState,
    prunedCriterion,

    toggleChallenge,
    setSelectionDifficulty,
    toggleCriterion,

    retry,
    freeze,
    unfreeze
  }
}
